import type { Decision } from "../../api/types";
import type { HistoryEntry } from "../../parse/status";
import { classifyHistoryLine } from "../../parse/status";
import { QuestionCard } from "../parts/QuestionCard";
import { texte } from "../../texte";

type FeedItem =
  | { kind: "history"; ts: string; text: string; historyKind: ReturnType<typeof classifyHistoryLine> }
  | { kind: "question"; ts: string; decision: Decision };

/** Verlauf: Karten-Feed aus /status-Historie + offenen/beantworteten Fragen, chronologisch. */
export function Feed({
  task,
  history,
  decisions,
  running,
  isAdmin,
  budgetHasRoom,
  onAnswer,
  onOpenVoucher,
}: {
  task?: string;
  history: HistoryEntry[];
  decisions: Decision[];
  running: boolean;
  isAdmin: boolean;
  /** Aktuelles Budget hat noch Reserve (Gutschein eingelöst o. Ä.) — dann gilt jede
   * "Budget erschöpft"-Karte im Verlauf als erledigt (§ unten). */
  budgetHasRoom: boolean;
  onAnswer: (qid: string, text: string) => void;
  onOpenVoucher: () => void;
}) {
  const items: FeedItem[] = [
    ...history.map((h) => ({
      kind: "history" as const,
      ts: h.ts,
      text: h.text,
      historyKind: classifyHistoryLine(h.text),
    })),
    ...decisions.map((d) => ({ kind: "question" as const, ts: d.asked_at, decision: d })),
  ].sort((a, b) => a.ts.localeCompare(b.ts));

  // Nur der JÜNGSTE "Budget erschöpft"-Eintrag kann noch unerledigt sein — ältere sind per
  // definitionem längst durch einen späteren Lauf/Gutschein überholt. Ist das Budget aktuell
  // wieder aufgefüllt (budgetHasRoom), gilt auch dieser als erledigt: unhighlighted, ohne Button.
  let lastBudgetIndex = -1;
  items.forEach((item, i) => {
    if (item.kind === "history" && item.historyKind === "stopped-budget") lastBudgetIndex = i;
  });

  return (
    <div className="feed">
      {task && (
        <div className="fcard task">
          <span className="ico">✎</span>
          <div className="body">
            <div className="label" style={{ marginBottom: 5 }}>
              {texte.feed.taskLabel}
            </div>
            <p className="goal">{task}</p>
          </div>
        </div>
      )}
      {items.map((item, i) => {
        if (item.kind === "question") {
          return <QuestionCard key={`q-${item.decision.qid}`} decision={item.decision} variant="feed" onAnswer={onAnswer} />;
        }
        if (item.historyKind === "stopped-budget") {
          const isUnresolved = i === lastBudgetIndex && !budgetHasRoom;
          if (!isUnresolved) {
            return (
              <div key={i} className="fcard">
                <span className="ico">·</span>
                <div className="body">
                  <p>
                    <b>{texte.feed.budgetExhausted}</b> {item.text}
                  </p>
                  <div className="ts">{item.ts}</div>
                </div>
              </div>
            );
          }
          return (
            <div key={i} className="fcard budget">
              <span className="ico">◑</span>
              <div className="body">
                <p>
                  <b>{texte.feed.budgetExhausted}</b> {item.text}
                </p>
                {isAdmin ? (
                  <div className="arow">
                    <button className="btn primary small" onClick={onOpenVoucher}>
                      {texte.feed.redeemVoucher}
                    </button>
                  </div>
                ) : (
                  <p className="hint" style={{ marginTop: 8 }}>
                    {texte.feed.askTeacher}
                  </p>
                )}
                <div className="ts">{item.ts}</div>
              </div>
            </div>
          );
        }
        if (item.historyKind === "stopped-done") {
          return (
            <div key={i} className="fcard done">
              <span className="ico">✓</span>
              <div className="body">
                <p>
                  <b>{texte.feed.done}</b> {item.text}
                </p>
                <div className="ts">{item.ts}</div>
              </div>
            </div>
          );
        }
        return (
          <div key={i} className="fcard">
            <span className="ico">·</span>
            <div className="body">
              <p>{item.text}</p>
              <div className="ts">{item.ts}</div>
            </div>
          </div>
        );
      })}
      {running && (
        <div className="fcard">
          <span className="ico">·</span>
          <div className="body">
            <span className="typing">
              <i />
              <i />
              <i />
            </span>{" "}
            <span className="hint">{texte.feed.agentWorking}</span>
          </div>
        </div>
      )}
    </div>
  );
}
